import SAT from 'sat';
import * as geometric from 'geometric';
import constants from '../../qti/Constants';

const POINTER_CROSS_TOLERANCE = 15;
const EXTRA_RESPONSE_IDENTIFIER = constants.extraResponseIdentifiers.HOTSPOT_INTERACTION.pointer;
const DEFAULT_WRONG_CHOICE_IDENTIFIER = 'hotspot_wrong_choice_identifier';

const hotSpotService = {
    getDefaultWrongChoiceIdentifier() { return DEFAULT_WRONG_CHOICE_IDENTIFIER; },
    isPointerStored(clickEventX, clickEventY, extraResponseCoords) {
        const pairXY = extraResponseCoords.split(' ');
        const x = parseInt(clickEventX, 10);
        const y = parseInt(clickEventY, 10);
        const xIsInRange = (pairXY[0] <= x + POINTER_CROSS_TOLERANCE) && (pairXY[0] >= x - POINTER_CROSS_TOLERANCE);
        const yIsInRange = (pairXY[1] <= y + POINTER_CROSS_TOLERANCE) && (pairXY[1] >= y - POINTER_CROSS_TOLERANCE);

        return (xIsInRange && yIsInRange);
    },

    isPointerInHotSpot(clickEventX, clickEventY, hotSpotArea) {
        const hotSpotCoords = hotSpotArea.coords.split(',');
        if (hotSpotArea.shape === 'polygon') {
            return geometric.pointInPolygon([clickEventX, clickEventY], this.getPolygonPoints(hotSpotCoords));
        }

        if (hotSpotArea.shape === 'circle') {
            // If hotspotCoords has length 3, it has the shape "circle" (center-x, center-y, radius)
            const point = new SAT.Vector(clickEventX, clickEventY);
            const circle = new SAT.Circle(new SAT.Vector(hotSpotCoords[0], hotSpotCoords[1]), hotSpotCoords[2]);
            return SAT.pointInCircle(point, circle);
        }
        // If hotspotCoords has length 4, it has the shape "rect"  (left-x, top-y, right-x, bottom-y)
        return (
            clickEventX >= parseInt(hotSpotCoords[0], 10) &&
            clickEventX <= parseInt(hotSpotCoords[2], 10) &&
            clickEventY >= parseInt(hotSpotCoords[1], 10) &&
            clickEventY <= parseInt(hotSpotCoords[3], 10)
        );
    },

    responseStackReachedMaxChoice(interactionModel, response = []) {
        if (interactionModel.cardinality === 'multiple' && interactionModel.maxChoices > 0) {
            return response.length === interactionModel.maxChoices;
        }
        return false;
    },

    addResponse(interactionModel, response, value) {
        if (this.responseStackReachedMaxChoice(interactionModel, response)) {
            // replacing the last added value
            this.replaceResponseAtIndex(response, response.length - 1, value);
        } else {
            response.push(value);
        }
    },

    replaceResponseAtIndex(response, index, value) {
        response.splice(index, 1, value);
    },

    deleteResponseAtIndex(response, index) {
        response.splice(index, 1);
    },

    getResponseCoordinates(interactionModel, extraResponses) {
        if (Array.isArray(extraResponses) && extraResponses.length > 0 && extraResponses[0] && typeof extraResponses[0] === 'object') {
            const coordinatesObject = extraResponses[0];
            if (interactionModel.cardinality === 'multiple' && !interactionModel.markHotspots) {
                return coordinatesObject[EXTRA_RESPONSE_IDENTIFIER];
            } else if (Array.isArray(coordinatesObject[EXTRA_RESPONSE_IDENTIFIER]) && coordinatesObject[EXTRA_RESPONSE_IDENTIFIER].length === 1) {
                // in this case, coordinatesObject looks like {[EXTRA_RESPONSE_IDENTIFIER]: ['123 321']}
                const coordinatesStringPair = coordinatesObject[EXTRA_RESPONSE_IDENTIFIER][0];
                const splitCoordinates = coordinatesStringPair.split(' ');
                if (splitCoordinates.length === 2) {
                    return [`${parseInt(splitCoordinates[0], 10)}, ${parseInt(splitCoordinates[1], 10)}`];
                }
            }
        }

        return [];
    },

    getIsAttempted(userResponse, hotSpotIdentifier) {
        let isAttempted = false;

        for (let i = 0; i < userResponse.length; i++) {
            const idx = userResponse[i].indexOf(hotSpotIdentifier);
            if (idx > -1) {
                isAttempted = true;
                break;
            }
        }

        return isAttempted;
    },

    getPointerIsAttempted(interactionModel, extraResponses) {
        let isAttempted = false;

        for (let i = 0; i < extraResponses.length; i++) {
            const extraResponse = extraResponses[i];
            if ({}.hasOwnProperty.call(extraResponse, EXTRA_RESPONSE_IDENTIFIER)) {
                isAttempted = true;
                break;
            }
        }

        if (interactionModel.regardUnansweredOutsideHotspot) {
            isAttempted = this.getPointerAnswerOption() !== '';
        }

        return isAttempted;
    },

    getPointerAnswerOption(userResponse) {
        if (userResponse && userResponse.length) {
            return userResponse[0];
        }

        return '';
    },

    showPointer(interactionModel, extraResponses, useKeyboard) {
        const responseCoordinates = this.getResponseCoordinates(interactionModel, extraResponses);

        return (
            (interactionModel.cardinality === 'single'
                || (interactionModel.cardinality === 'multiple' && !interactionModel.markHotspots))
            && (responseCoordinates.length || useKeyboard)
        );
    },

    getPolygonPoints(points) {
        return points.filter(i => i !== '').map((item) => {
            const [x, y] = item.split('y').map(i => i.split(':'));
            return [parseInt(x[1], 10), parseInt(y[1], 10)];
        });
    },
};

export default hotSpotService;
